import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import SelectWithValidation from '../../ValidationFormElements/SelectWithValidation';
import { bindTransportUsageTypesSelectOption } from '../../../helpers/selectHelpers';
import ColorInput from '../../../containers/RoadCareContractContainer/steps/SearchPage/ColorInput';
import ModelInput from '../../../containers/RoadCareContractContainer/steps/SearchPage/ModelInput';
import useDeviceResize from '../../../hooks/useDeviceResize';

const VehicleInfo = ({
  formProps,
  transportUsageTypes,
  formValues,
  vehicleManufactures,
  modelNames,
  releaseYears,
}) => {
  const { errors, clearError } = formProps;
  const { i18n } = useTranslation();
  const isDeviceSmSize = useDeviceResize('sm');

  const colorInput = useCallback(() => (
    <ColorInput clearError={clearError} errors={errors} formValues={formValues} />
  ), [clearError, errors, formValues]);

  const modelInput = useCallback(() => (
    <ModelInput
      clearError={clearError}
      errors={errors}
      formProps={formProps}
      formValues={formValues}
      modelNames={modelNames}
    />
  ), [clearError, errors, formProps, formValues, modelNames]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SelectWithValidation
            required
            disabled
            name="manufacturerName"
            label="transportation.manufacturerName"
            value={formValues.manufacturerName}
            formProps={{
              errors,
              clearError,
            }}
            getMenuLabel={(option) => option.label}
            options={vehicleManufactures}
            onChange={() => {}}
          />
        </Grid>
        {
          isDeviceSmSize ? (
            <>
              {modelInput()}
              {colorInput()}
            </>
          ) : (
            <>
              {colorInput()}
              {modelInput()}
            </>
          )
        }
        <Grid item xs={12} sm={6}>
          <SelectWithValidation
            required
            disabled
            name="transportUseType"
            label="transportation.vehicleType"
            value={formValues.transportUseType}
            formProps={{
              errors,
              clearError,
            }}
            getMenuLabel={(option) => (option.label ? option.label[i18n.language] : '')}
            options={bindTransportUsageTypesSelectOption(transportUsageTypes)}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInputFieldWithValidation
            required
            formProps={{
              errors,
              clearError,
            }}
            labelKey="transportation.horsePower"
            name="horsePower"
            value={formValues.horsePower}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInputFieldWithValidation
            required
            formProps={{
              errors,
              clearError,
            }}
            labelKey="transportation.passport"
            name="passport"
            value={formValues.passport}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectWithValidation
            required
            name="releaseYear"
            label="transportation.releaseYear"
            value={formValues.releaseYear}
            disabled
            formProps={{
              errors,
              clearError,
            }}
            getMenuLabel={(option) => option.label}
            options={releaseYears}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInputFieldWithValidation
            required
            formProps={{
              errors,
              clearError,
            }}
            labelKey="transportation.vin"
            name="vin"
            value={formValues.vin}
            disabled
          />
        </Grid>
      </Grid>
    </>
  );
};

VehicleInfo.propTypes = {
  formProps: PropTypes.shape({
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
  }).isRequired,
  formValues: PropTypes.shape({
    color: PropTypes.string,
    vin: PropTypes.string,
    modelName: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
    }),
    manufacturerName: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
    }),
    transportUseType: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.shape({
        en: PropTypes.string,
        hy: PropTypes.string,
        ru: PropTypes.string,
      }),
    }),
    releaseYear: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
    }),
    horsePower: PropTypes.string,
    passport: PropTypes.string,
  }).isRequired,
  transportUsageTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  vehicleManufactures: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  modelNames: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  releaseYears: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default VehicleInfo;
